import {CardSnapshots,type CardCacheScope} from './card-snapshots.js';
import {pmTaskAssetPath} from '../shared/pm-task-preview.js';
import { readPmMessageSnapshot, type PmMessageSnapshot } from '../shared/pm-message-snapshot.js';
import { renderPmTaskCard, renderPmReauthenticationHint, renderPmTaskUnavailable, renderPmTaskLoading } from './pm-task-card.js';

export type PmSnapshotRequest = { messageId: string; taskId: string; messageVersion: number;
  refresh?: { expectedSnapshotRevision: string; attemptId: string } };
type Slot = { host: HTMLElement; request: PmSnapshotRequest; key: string; pending: boolean;
  reauthenticate: boolean; controller: AbortController | null; snapshot: PmMessageSnapshot | null };
/** Mounted viewer-owned snapshots only. Restored display remains fenced by current Chat access; every network read is server authorized. */
export class PmTaskPreviews {
  private owner = '';
  private cache = new CardSnapshots<PmMessageSnapshot>('pm-chat:task-cards:v1',readCachedSnapshot);
  private cacheContext = '';
  private requiresReauthentication = false;
  private reauthenticationHint: HTMLElement | null = null;
  private history: HTMLElement | null = null;
  private valid: () => boolean = () => false;
  private canLoad: () => boolean = () => false;
  private slots = new Map<HTMLElement, Slot>();
  private inflight = 0;
  constructor(private readonly load: (request: PmSnapshotRequest, signal: AbortSignal) => Promise<unknown>,
    private readonly change: (mutate: () => void) => void = mutate => mutate()) {}

 forgetConversation(id:string){const prefix=JSON.stringify([id]).slice(0,-1)+',';this.cache.forgetContext(prefix);if(this.cacheContext.startsWith(prefix))this.detach();}
  clear(): void {this.detach();this.cache.clear();this.cacheContext='';}
  detach(): void {
    this.owner = ''; this.valid = () => false; this.canLoad = () => false;
    this.change(() => {
      this.reauthenticationHint?.remove(); this.reauthenticationHint = null;
      for (const slot of this.slots.values()) { slot.controller?.abort(); slot.host.replaceChildren(); }
    });
    this.slots.clear(); this.history = null;
  }
  sync(history: HTMLElement | null, owner: string, valid: () => boolean, requiresReauthentication = false,
    canLoad: () => boolean = valid, cacheScope:CardCacheScope={owner,context:owner}): void {
    const cacheChanged=this.cache.align(cacheScope.owner);const contextChanged=this.cacheContext!==cacheScope.context;this.cacheContext=cacheScope.context;
    if (cacheChanged || contextChanged || owner !== this.owner || requiresReauthentication !== this.requiresReauthentication) { this.detach(); this.owner = owner; }
    this.requiresReauthentication = requiresReauthentication; this.valid = valid; this.canLoad = canLoad;
    if (!history || !owner || !valid()) { this.detach(); return; }
    this.history = history;
    const wanted = new Set([...history.querySelectorAll<HTMLElement>('.pm-task-slot')].slice(0, 64));
    const removed = new Map([...this.slots].filter(([host]) => !wanted.has(host)));
    // A receipt/reaction may replace the article while the message/version/task and
    // authorized owner stay the same. Move its existing card and decoded images, not
    // another snapshot request. Unmatched old slots are purged.
    for (const host of wanted) if (!this.slots.has(host)) {
      const taskId = host.dataset.taskId, messageId = host.dataset.messageId, messageVersion = Number(host.dataset.messageVersion);
      if (!taskId || !/^[1-9]\d{0,9}$/.test(taskId) || Number(taskId) > 2147483647 || !messageId
        || !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(messageId)
        || !Number.isInteger(messageVersion) || messageVersion <= 0 || messageVersion > 2147483647) continue;
      const key = `${messageId}:${messageVersion}:${taskId}`;
      const previous = [...removed].find(([, slot]) => slot.key === key);
      if (previous) this.change(() => {
        const [retired, slot] = previous;
        host.replaceChildren(...retired.childNodes);
        removed.delete(retired); this.slots.delete(retired); slot.host = host; this.slots.set(host, slot);
      });
      else {const snapshot=this.requiresReauthentication?null:this.cache.get(`${this.cacheContext}:${key}`)??null;
        const slot:Slot={host,request:{messageId,taskId,messageVersion},key,pending:!snapshot,reauthenticate:false,controller:null,snapshot};this.slots.set(host,slot);
        if(snapshot)this.change(()=>this.showCard(slot,owner));
      }
    }
    if (removed.size) this.change(() => {
      for (const [host, slot] of removed) { slot.controller?.abort(); host.replaceChildren(); this.slots.delete(host); }
    });
    this.placeReauthenticationHint(); this.pump();
  }
  private placeReauthenticationHint(): void {
    if (!this.history || !this.requiresReauthentication) return;
    const bounds = this.history.getBoundingClientRect();
    const visible = (node: HTMLElement): boolean => {
      const rect = node.getBoundingClientRect();
      return rect.height > 0 && rect.bottom > Math.max(0, bounds.top) && rect.top < Math.min(innerHeight, bounds.bottom);
    };
    if (this.reauthenticationHint?.isConnected && visible(this.reauthenticationHint)) return;
    const slot = [...this.slots.values()].find(item => item.reauthenticate && visible(item.host.closest<HTMLElement>('.message') ?? item.host));
    if (!slot) { if (this.reauthenticationHint?.isConnected) this.change(() => this.reauthenticationHint?.remove()); return; }
    this.reauthenticationHint ??= renderPmReauthenticationHint();
    if (this.reauthenticationHint.parentElement !== slot.host) this.change(() => slot.host.replaceChildren(this.reauthenticationHint!));
  }
  private current(slot: Slot, owner: string, controller?: AbortController): boolean {
    return owner === this.owner && this.valid()
      && this.slots.get(slot.host) === slot && (!controller || slot.controller === controller) && slot.host.isConnected;
  }
  private showCard(slot: Slot, owner: string, refreshing = false, refreshFailed = false): void {
    if (!slot.snapshot) return;
    slot.host.replaceChildren(renderPmTaskCard(slot.snapshot.card, Date.parse(slot.snapshot.fetchedAt), () => {
      if (!this.current(slot, owner) || !this.canLoad() || document.visibilityState === 'hidden' || slot.controller || slot.pending || !slot.snapshot) return;
      slot.request.refresh = { expectedSnapshotRevision: slot.snapshot.snapshotRevision, attemptId: crypto.randomUUID() };
      slot.pending = true; this.pump();
    }, { refreshing, refreshFailed }));
  }
  private showFailure(slot: Slot, owner: string, error: unknown, refreshing: boolean): void {
    const status = (error as { status?: number })?.status;
    slot.pending = false;
    if (refreshing && slot.snapshot && ![401, 404, 409].includes(status ?? 0)) {
      this.showCard(slot, owner, false, true); return;
    }
    this.cache.delete(`${this.cacheContext}:${slot.key}`);slot.snapshot = null;
    slot.reauthenticate = this.requiresReauthentication && status === 401;
    if (slot.reauthenticate) { slot.host.replaceChildren(); return; }
    slot.host.replaceChildren(renderPmTaskUnavailable(slot.request.taskId, () => {
      if (!this.current(slot, owner) || !this.canLoad() || document.visibilityState === 'hidden' || slot.controller || slot.pending) return;
      slot.pending = true; this.pump();
    }));
  }
  private pump(): void {
    if (!this.valid()) { this.detach(); return; }
    if (!this.canLoad() || document.visibilityState === 'hidden') return;
    for (const slot of this.slots.values()) {
      if (this.inflight >= 3) break;
      if (!slot.pending || slot.controller || !slot.host.isConnected) continue;
      if ([...this.slots.values()].some(item => item.key === slot.key && item.controller)) continue;
      slot.pending = false; slot.reauthenticate = false;
      const controller = new AbortController(); slot.controller = controller;
      const owner = this.owner, request = { ...slot.request }, refreshing = Boolean(request.refresh);
      delete slot.request.refresh;
      this.change(() => {
        if (refreshing && slot.snapshot) this.showCard(slot, owner, true);
        else {
          slot.host.replaceChildren(renderPmTaskLoading());
        }
      });
      let timedOut = false;
      const timeout = setTimeout(() => { timedOut = true; controller.abort(); }, 15_000);
      this.inflight += 1;
      void this.load(request, controller.signal).catch(error => {
        if (refreshing && (error as { status?: number })?.status === 409
          && !controller.signal.aborted && this.current(slot, owner, controller)) {
          // Another tab may have won CAS. Reconcile once from storage; never repeat POST.
          return this.load({ messageId: request.messageId, taskId: request.taskId, messageVersion: request.messageVersion }, controller.signal);
        }
        throw error;
      }).then(value => {
        if (controller.signal.aborted || !this.current(slot, owner, controller)) return;
        const snapshot = readPmMessageSnapshot(value, request.messageId, request.taskId, request.messageVersion);
        this.change(() => {
          for (const item of this.slots.values()) if (item.key === slot.key && item.host.isConnected) {
            if (snapshot) { this.cache.set(`${this.cacheContext}:${item.key}`,snapshot);item.snapshot = snapshot; item.pending = false; item.reauthenticate = false; this.showCard(item, owner); }
            else this.showFailure(item, owner, null, refreshing);
          }
        });
      }).catch(error => {
        if ((!controller.signal.aborted || timedOut) && this.current(slot, owner, controller)) this.change(() => {
          for (const item of this.slots.values()) if (item.key === slot.key && item.host.isConnected) this.showFailure(item, owner, error, refreshing);
        });
      }).finally(() => {
        clearTimeout(timeout); this.inflight -= 1;
        if (slot.controller === controller) slot.controller = null;
        this.placeReauthenticationHint(); this.pump();
      });
    }
  }
}

/** Restore only the exact message-bound asset locators that the network parser generated. */
function readCachedSnapshot(value:unknown,key:string):PmMessageSnapshot|null{
 try{
  const [messageId,version,taskId]=key.split(':').slice(-3);if(!messageId||!taskId)return null;
  const input=structuredClone(value) as PmMessageSnapshot;
  if(!input?.card?.project)return null;
  const asset=(kind:string)=>`/v1/messages/${messageId}/pm-task-previews/${taskId}/assets/${kind}?message_version=${Number(version)}&snapshot_revision=${input.snapshotRevision}`;
  if(input.card.project.iconUrl!==null){if(input.card.project.iconUrl!==asset('project-icon'))return null;input.card.project.iconUrl=pmTaskAssetPath(taskId,'project-icon');}
  if(input.card.assignee&&input.card.assignee.avatarUrl!==null){if(input.card.assignee.avatarUrl!==asset('assignee-avatar'))return null;input.card.assignee.avatarUrl=pmTaskAssetPath(taskId,'assignee-avatar');}
  return readPmMessageSnapshot(input,messageId,taskId,Number(version));
 }catch{return null;}
}
